import React, { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { UsageIndicator } from 'src/components/common/UsageIndicator';
import { usePublicTrial } from 'src/contexts/PublicTrialContext';
import { useUsage } from 'src/hooks/useUsage';
import { ReportNotes } from 'src/models/reports';
import {
  downloadBase64Document, downloadReportsAsWord, generateCommentFromNotes
} from 'src/services/reports.service';
import { CommentBank } from './comment-bank';
import { clearReportSession, loadReportSession, saveReportSession, StudentDraft } from './report-comments-session';
import 'src/styles/components/forms/report-notes.css';

const newStudent = (id: number): StudentDraft => ({
  id, name: '', pronouns: 'they/them', notes: '', selectedComments: []
});
const isComplete = (student: StudentDraft) => Boolean(
  student.name.trim() && (student.notes.trim() || student.selectedComments.length)
);
const characterCount = (text: string) => Array.from(text).length;
const canExport = (student: StudentDraft) => Boolean(student.result?.comment.trim()
  && characterCount(student.result.comment) <= student.result.characterLimit);

export const ReportCommentsForm: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { isPublicTrial, requestTrialSignup } = usePublicTrial();
  const { getUsageForProduct, usageSummary, loading: usageLoading, refresh: refreshUsage } = useUsage();
  const usage = getUsageForProduct('reports');
  const formRef = useRef<HTMLFormElement>(null);
  const statusRef = useRef<HTMLDivElement>(null);
  const scrollToStudentId = useRef<number | null>(null);
  const focusStudentId = useRef<number | null>(null);
  const [restored] = useState(loadReportSession);
  const nextId = useRef(restored ? Math.max(...restored.students.map(student => student.id)) + 1 : 2);
  const [students, setStudents] = useState<StudentDraft[]>(restored?.students || [newStudent(1)]);
  const [collapsedStudentIds, setCollapsedStudentIds] = useState<number[]>([]);
  const [characterLimit, setCharacterLimit] = useState(restored?.characterLimit || '');
  const [generatingId, setGeneratingId] = useState<number | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [copiedComment, setCopiedComment] = useState<{ id: number; comment: string } | null>(null);
  const limit = characterLimit.trim() === '' ? 1000 : Number(characterLimit);
  const validLimit = Number.isInteger(limit) && limit >= 100 && limit <= 5000;
  const canUse = isPublicTrial || (!usageLoading && Boolean(usage?.can_use));
  const pending = students.filter(student => !student.result);
  const results = students.filter(student => student.result);
  const usagePeriod = (usageSummary?.period ? new Date(`${usageSummary.period}-01T00:00:00`) : new Date())
    .toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });

  useEffect(() => { clearReportSession(); }, []);

  useEffect(() => {
    if (focusStudentId.current === null) return;
    const input = formRef.current?.querySelector<HTMLInputElement>(`#student-${focusStudentId.current}-name`);
    input?.focus({ preventScroll: true });
    input?.scrollIntoView?.({ block: 'center' });
    focusStudentId.current = null;
  }, [students.length]);

  useEffect(() => {
    if (!copiedComment) return;
    const timer = window.setTimeout(() => setCopiedComment(null), 2000);
    return () => window.clearTimeout(timer);
  }, [copiedComment]);

  useEffect(() => {
    if (isGenerating) {
      statusRef.current?.scrollIntoView?.({ block: 'center' });
    } else if (scrollToStudentId.current !== null) {
      formRef.current?.querySelector(`#comment-${scrollToStudentId.current}`)?.scrollIntoView?.({ block: 'center' });
      scrollToStudentId.current = null;
    }
  }, [isGenerating]);

  const requestExportAccess = () => requestTrialSignup({
    purpose: 'report-export',
    returnTo: '/dashboard#reports',
    beforeRedirect: () => saveReportSession(students, characterLimit),
  });

  const updateStudent = (id: number, update: Partial<StudentDraft>) => {
    setStudents(current => current.map(student => student.id === id ? { ...student, ...update } : student));
    setNotice('');
  };

  const generate = async (targets: StudentDraft[]) => {
    if (isGenerating || !validLimit || !canUse || !targets.length || targets.some(student => !isComplete(student))) return;
    formRef.current?.querySelectorAll<HTMLDetailsElement>('.report-notes__bank').forEach(bank => {
      bank.open = false;
    });
    setError('');
    setNotice('');
    setCopiedComment(null);
    setCollapsedStudentIds(current => current.filter(id => !targets.some(student => student.id === id)));
    setIsGenerating(true);
    let completed = 0;
    try {
      const token = isPublicTrial ? null : await getAccessTokenSilently();
      for (const student of targets) {
        // Each call spends one report credit; stop at the known remaining allowance.
        if (!isPublicTrial && usage?.remaining != null && completed >= usage.remaining) {
          setError('Your remaining report allowance has been used. Completed comments are still available.');
          break;
        }
        setGeneratingId(student.id);
        updateStudent(student.id, { error: undefined });
        const response = await generateCommentFromNotes(student, limit, token);
        if (response.error || !response.data) {
          updateStudent(student.id, { error: response.error?.message || 'Could not generate this comment. Please try again.' });
          break;
        }
        updateStudent(student.id, { result: { ...response.data, characterLimit: limit } });
        if (completed === 0) scrollToStudentId.current = student.id;
        completed += 1;
        if (!isPublicTrial) refreshUsage();
      }
      if (completed && !isPublicTrial) setNotice(`${completed} ${completed === 1 ? 'comment is' : 'comments are'} ready to review.`);
    } catch {
      setError('Could not generate the remaining comments. Your notes and completed comments are still here. Please try again.');
    } finally {
      setGeneratingId(null);
      setIsGenerating(false);
    }
  };

  const download = async () => {
    if (isPublicTrial) {
      requestExportAccess();
      return;
    }
    setIsDownloading(true);
    setError('');
    try {
      const token = await getAccessTokenSilently();
      const reports = results.map(student => ({ studentName: student.result!.studentName, comment: student.result!.comment }));
      const response = await downloadReportsAsWord(reports, token, 'report');
      if (response.error || !response.data?.document) {
        setError(response.error?.message || 'Could not download comments. Please try again.');
      } else {
        downloadBase64Document(response.data.document, response.data.filename);
      }
    } catch {
      setError('Could not download comments. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const copy = async (id: number, comment: string) => {
    if (isPublicTrial) {
      requestExportAccess();
      return;
    }
    setError('');
    setNotice('');
    setCopiedComment(null);
    try {
      await navigator.clipboard.writeText(comment);
      setCopiedComment({ id, comment });
      setNotice('Comment copied.');
    } catch {
      setError('Could not copy automatically. Select the comment text and copy it manually.');
    }
  };

  const addStudentButton = (
    <button type="button" className="report-notes__secondary" disabled={isGenerating} onClick={() => {
      const student = newStudent(nextId.current++);
      setCollapsedStudentIds(current => Array.from(new Set([
        ...current, ...students.filter(item => canExport(item) && !item.error).map(item => item.id)
      ])));
      focusStudentId.current = student.id;
      setStudents(current => [...current, student]);
      setNotice('');
      setCopiedComment(null);
    }}>+ Add another student</button>
  );

  // Keep student fields, labels and generated comments out of PostHog replay and autocapture.
  return (
    <div className="report-notes ph-no-capture">
      <form ref={formRef} onSubmit={event => { event.preventDefault(); void generate(pending); }}>
        <fieldset className="report-notes__inputs" disabled={isGenerating}>
          {students.map((student, index) => {
            const result = student.result;
            const isCollapsed = collapsedStudentIds.includes(student.id);
            const count = result ? characterCount(result.comment) : 0;
            return <section className={`report-notes__student${isCollapsed ? ' report-notes__student--collapsed' : ''}`}
              key={student.id} aria-labelledby={`student-${student.id}-title`}>
              <div className="report-notes__student-heading">
                <h3 id={`student-${student.id}-title`}>
                  {result ? (
                    <button type="button" className="report-notes__student-toggle" aria-expanded={!isCollapsed}
                      aria-controls={`student-${student.id}-content`} onClick={() => setCollapsedStudentIds(current =>
                        isCollapsed ? current.filter(id => id !== student.id) : [...current, student.id])}>
                      <span>Student {index + 1} · {result.studentName}</span>
                      {canExport(student) && !student.error && <span className="report-notes__completed" role="img" aria-label="Completed" title="Completed">✓</span>}
                      <span className="report-notes__chevron" aria-hidden="true">{isCollapsed ? '▸' : '▾'}</span>
                    </button>
                  ) : `Student ${index + 1}`}
                </h3>
                {students.length > 1 && <button type="button" className="report-notes__text-button"
                  onClick={() => {
                    setStudents(current => current.filter(item => item.id !== student.id));
                    setCollapsedStudentIds(current => current.filter(id => id !== student.id));
                  }}
                  aria-label={`Remove student ${index + 1}`}>Remove</button>}
              </div>
              <fieldset id={`student-${student.id}-content`} className="report-notes__inputs" hidden={isCollapsed} disabled={isCollapsed}>
                <div className="report-notes__row">
                  <div className="report-notes__field">
                    <label htmlFor={`student-${student.id}-name`}>Student name</label>
                    <input id={`student-${student.id}-name`} type="text" maxLength={100} required autoComplete="off" placeholder="First name"
                      value={student.name} onChange={event => updateStudent(student.id, { name: event.target.value })} />
                  </div>
                  <div className="report-notes__field">
                    <label htmlFor={`student-${student.id}-pronouns`}>Pronouns</label>
                    <select id={`student-${student.id}-pronouns`} value={student.pronouns}
                      onChange={event => updateStudent(student.id, { pronouns: event.target.value as ReportNotes['pronouns'] })}>
                      <option value="they/them">They / them</option><option value="she/her">She / her</option><option value="he/him">He / him</option>
                    </select>
                  </div>
                </div>
                <div className="report-notes__field">
                  <label htmlFor={`student-${student.id}-notes`}>Notes about this student</label>
                  <textarea id={`student-${student.id}-notes`} rows={5} maxLength={10000} value={student.notes}
                    placeholder={'e.g.\n• kind, works well with others\n• reading more fluently, loves discussing stories\n• confident with times tables\n• next step: check work before handing in'}
                    onChange={event => updateStudent(student.id, { notes: event.target.value })} />
                </div>
                <CommentBank id={`student-${student.id}`} selected={student.selectedComments}
                  onChange={selectedComments => updateStudent(student.id, { selectedComments })} />
                {student.error && <p className="report-notes__error" role="alert">{student.error}</p>}
                {result && <div className="report-notes__result">
                  <label htmlFor={`comment-${student.id}`}>{result.studentName}</label>
                  <textarea id={`comment-${student.id}`} rows={6} value={result.comment} disabled={isGenerating || isDownloading} readOnly={isPublicTrial}
                    onCopy={event => { if (isPublicTrial) { event.preventDefault(); requestExportAccess(); } }}
                    onCut={event => { if (isPublicTrial) { event.preventDefault(); requestExportAccess(); } }}
                    onChange={event => updateStudent(student.id, { result: { ...result, comment: event.target.value } })} />
                  <div className="report-notes__result-footer">
                    <span className={count > result.characterLimit ? 'report-notes__error' : 'report-notes__hint'}>{count} / {result.characterLimit} characters{count > result.characterLimit ? ' — shorten before copying or downloading' : ''}</span>
                    <div className="report-notes__actions">
                      {!isPublicTrial && <button type="button" className="report-notes__secondary" disabled={isGenerating || !canExport(student)}
                        onClick={() => void copy(student.id, result.comment)}>
                        {copiedComment?.id === student.id && copiedComment.comment === result.comment ? 'Copied!' : 'Copy comment'}
                      </button>}
                      <button type="button" className="report-notes__text-button" disabled={isGenerating || !canUse || !validLimit || !isComplete(student)}
                        onClick={() => void generate([student])}>Regenerate</button>
                    </div>
                  </div>
                </div>}
              </fieldset>
            </section>;
          })}
          <div className="report-notes__actions report-notes__form-actions">
            {results.length === 0 && addStudentButton}
            <div className="report-notes__field report-notes__limit">
              <label htmlFor="report-character-limit">Character limit <span>(optional)</span></label>
              <input id="report-character-limit" type="number" min="100" max="5000" step="1" value={characterLimit}
                placeholder="Default: 1000" onChange={event => setCharacterLimit(event.target.value)} />
            </div>
            {pending.length > 0 && <button type="submit" className="report-notes__primary"
              disabled={isGenerating || !canUse || !validLimit || pending.some(student => !isComplete(student))}>
              {isGenerating ? 'Generating…' : `Generate ${pending.length === 1 ? 'comment' : `${pending.length} comments`}`}
            </button>}
          </div>
        </fieldset>
      </form>
      <div ref={statusRef} role="status" aria-live="polite">
        {isGenerating ? <p>Writing a comment for {students.find(student => student.id === generatingId)?.name || 'your student'}…</p> : notice && <p>{notice}</p>}
      </div>
      {error && <p className="report-notes__error" role="alert">{error}</p>}
      {results.length > 0 && <div className="report-notes__actions">
        {addStudentButton}
        {isPublicTrial ? (
          <button type="button" className="report-notes__primary" disabled={isGenerating} onClick={requestExportAccess}>
            Create a free account to copy or download
          </button>
        ) : (
          <button type="button" className="report-notes__secondary" disabled={isDownloading || isGenerating || results.some(student => !canExport(student))}
            onClick={() => void download()}>{isDownloading ? 'Downloading…' : 'Download comments as Word'}</button>
        )}
      </div>}
      {!isPublicTrial && (usage || usageLoading ? (
        <UsageIndicator productName="Report comments" used={usage?.used ?? 0} limit={usage?.limit ?? null}
          remaining={usage?.remaining ?? null} canUse={usage?.can_use ?? false} period={usagePeriod}
          loading={usageLoading && !usage} />
      ) : (
        <section className="usage-indicator" aria-label="Report comments usage">
          <div className="usage-heading"><span className="usage-title">Monthly report comments usage</span></div>
          <div className="usage-meta" role="status">
            Usage information is currently unavailable.{' '}
            <button type="button" className="report-notes__text-button" onClick={refreshUsage}>Try again</button>
          </div>
        </section>
      ))}
    </div>
  );
};
